import { useMemo, useState } from 'react';
import { ChevronDown, Loader2, Plus, Search, Tag } from 'lucide-react';
import { useItems } from '@repo/queries';
import type { Service } from '@repo/api-client/dtos';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { AiPill } from '@/features/ai/AiPill';
import { useAiComposer } from '@/features/ai/AiComposerContext';
import { DataTable } from '@/features/table/DataTable';
import { displayWireMoney } from '@/features/money';
import { describeAppError } from '@/features/common/describeAppError';
import { Alert } from '@/components/ui/alert';
import { ServiceFormDialog } from '@/features/service/ServiceFormDialog';
import { useActiveRole } from '@/features/auth/useActiveRole';

export function ServicesListScreen() {
  const { canEditOrg } = useActiveRole();
  const { items, loading, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useItems();
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<Service | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { open: openAi } = useAiComposer();

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) => s.name.toLowerCase().includes(q));
  }, [items, search]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }: { row: { original: Service } }) => (
          <div>
            <div className="font-medium text-foreground">{row.original.name}</div>
            {row.original.description ? (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {row.original.description}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: 'pricingRate',
        header: () => <span className="text-right">Rate</span>,
        cell: ({ row }: { row: { original: Service } }) => (
          <span className="block text-right font-mono text-sm">
            {displayWireMoney(row.original.pricingRate, row.original.pricingCurrency)}
            <span className="ml-1 text-xs text-muted-foreground">
              · {row.original.pricingCustomUnit ?? row.original.pricingUnit}
            </span>
          </span>
        ),
      },
      {
        accessorKey: 'taxable',
        header: 'Tax',
        cell: ({ row }: { row: { original: Service } }) => (
          <Badge variant={row.original.taxable ? 'default' : 'outline'}>
            {row.original.taxable
              ? row.original.taxRate
                ? `${row.original.taxRate}%`
                : 'Taxable'
              : 'Exempt'}
          </Badge>
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }: { row: { original: Service } }) => (
          <Badge variant={row.original.isActive ? 'success' : 'outline'}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
    ],
    [],
  );

  return (
    <Page>
      <PageHeader
        title="Services"
        description="Reusable line items so you stop retyping prices when invoicing."
        actions={
          canEditOrg ? (
            <Button
              onClick={() => {
                setActive(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="size-4" /> New service
            </Button>
          ) : null
        }
      />

      {canEditOrg ? (
        <AiPill className="mb-5" onClick={() => openAi()} />
      ) : null}

      <div className="mb-4 flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search services"
            className="pl-9"
          />
        </div>
      </div>

      {error ? (
        <Alert variant="danger" className="mb-4">
          {describeAppError(error).message}
        </Alert>
      ) : null}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, idx) => (
            <Skeleton key={idx} className="h-14 rounded-lg" />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          onRowClick={
            canEditOrg
              ? (row) => {
                  setActive(row);
                  setDialogOpen(true);
                }
              : undefined
          }
          emptyState={
            <EmptyState
              icon={<Tag className="size-7" />}
              title={search ? 'No services match' : 'No services yet'}
              description={
                search
                  ? 'Try a different search term.'
                  : 'Create your first reusable rate.'
              }
              actions={
                search || !canEditOrg ? null : (
                  <Button
                    onClick={() => {
                      setActive(null);
                      setDialogOpen(true);
                    }}
                  >
                    <Plus className="size-4" /> New service
                  </Button>
                )
              }
            />
          }
        />
      )}

      {hasNextPage ? (
        <div className="mt-4 flex justify-center">
          <Button variant="outline" onClick={fetchNextPage} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <ChevronDown className="size-4" />
            )}
            Load more
          </Button>
        </div>
      ) : null}

      <ServiceFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setActive(null);
        }}
        service={active}
      />
    </Page>
  );
}
